import {
	SIGNATURE_BLOCK_SELECTOR,
	SIGNATURE_COLUMN_ID_PREFIX,
	SIGNATURE_ROW_ID_PREFIX,
} from '@/components/UnlayerEditorWrapper/signatureDesign/constants';

// Unlayer design objects are deeply nested, so clone before changing structure.
export const cloneDesignSnapshot = (value) => JSON.parse(JSON.stringify(value));
const SIGNATURE_CONTENT_ID_PREFIX = 'signature_';

const normalizeManagedId = (id, fromPrefix, toPrefix) => {
	const stringId = String(id ?? '');
	if (!stringId.startsWith(fromPrefix)) {
		return stringId;
	}
	return `${toPrefix}${stringId.slice(fromPrefix.length)}`;
};

// Remove the DOM wrapper we previously inserted around a signature so selecting
// a new signature replaces the old one instead of stacking duplicates.
export const removeSignatureBlockFromHtml = (html) => {
	if (typeof html !== 'string' || html.length === 0) {
		return '';
	}

	try {
		const container = document.createElement('div');
		container.innerHTML = html;
		container.querySelectorAll(SIGNATURE_BLOCK_SELECTOR).forEach((node) => node.remove());
		return container.innerHTML.trim();
	} catch (error) {
		return html;
	}
};

// Wrap arbitrary signature html in a marker element so later replacements can
// find exactly the signature block without disturbing user-authored content.
export const getSignatureBlock = (signatureHtml) => {
	if (typeof signatureHtml !== 'string') {
		return '';
	}

	const trimmed = signatureHtml.trim();
	if (!trimmed) {
		return '';
	}

	if (trimmed.includes('data-signature="true"') || trimmed.includes('id="email-signature"')) {
		return trimmed;
	}

	return `<div data-signature="true">${trimmed}</div>`;
};

// Reuse an existing html content block as a metadata template because Unlayer
// expects html nodes to carry internal metadata fields alongside the raw html.
export const findHtmlTemplate = (rows) => {
	for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
		const columns = rows?.[rowIndex]?.columns;
		if (!Array.isArray(columns)) {
			continue;
		}

		for (let columnIndex = 0; columnIndex < columns.length; columnIndex += 1) {
			const contents = columns?.[columnIndex]?.contents;
			if (!Array.isArray(contents)) {
				continue;
			}

			const htmlContent = contents.find(
				(content) => content?.type === 'html' && typeof content?.values === 'object'
			);
			if (htmlContent) {
				return htmlContent;
			}
		}
	}

	return null;
};

// Strip old signature rows and old signature html blocks so the next insertion
// always rebuilds the design into a single, deterministic signature state.
//
// A signature is encased in the following structure:
// 	- signature_row_xxx
//		- signature_column_xxx 
//			- signature_xxx
// There is a case where if a user is in the Compose Email dialog, and
// crafts an template to save, they are likely adding the content within
// the signature_row_xxx row, rather than create a new row first
//
// In that case, we need to check if there is are any content without an id of signature_xxx
// and in that case:
// - conver the row and columns into non signature row and columns (just rename the ids)
// - remove the signature content, but leave the rest alone
export const removeSignatureArtifacts = (rows) => {
	const nextRows = rows.map((row) => {
		const rowId = String(row?.id ?? '');
		const isManagedRow = rowId.startsWith(SIGNATURE_ROW_ID_PREFIX);
		const columns = row?.columns;
		if (!Array.isArray(columns)) {
			if (!isManagedRow) {
				return row;
			}
			return {
				...row,
				id: normalizeManagedId(rowId, SIGNATURE_ROW_ID_PREFIX, 'row_'),
			};
		}

		const nextColumns = columns.map((column) => {
			const columnId = String(column?.id ?? '');
			const isManagedColumn = columnId.startsWith(SIGNATURE_COLUMN_ID_PREFIX);
			const contents = column?.contents;
			if (!Array.isArray(contents)) {
				return column;
			}

			const nextContents = contents
				.map((content) => {
					if (content?.type !== 'html' || !content?.values) {
						return content;
					}

					const originalHtml = content.values.html;
					if (typeof originalHtml !== 'string') {
						return content;
					}

					return {
						...content,
						values: {
							...content.values,
							html: removeSignatureBlockFromHtml(originalHtml),
						},
					};
				})
				.filter((content) => {
					if (String(content?.id ?? '').startsWith(SIGNATURE_CONTENT_ID_PREFIX)) {
						return false;
					}
					if (content?.type !== 'html') {
						return true;
					}

					const html = content?.values?.html;
					return !(typeof html === 'string' && html.trim().length === 0);
				});

			const nextColumn = {
				...column,
				contents: nextContents,
			};

			// Managed column ids are reserved for auto-inserted signature rows.
			// If user-authored content remains after cleanup, rename the id so the
			// row is no longer treated as an auto-managed signature container.
			if (isManagedColumn && nextContents.length > 0) {
				nextColumn.id = normalizeManagedId(columnId, SIGNATURE_COLUMN_ID_PREFIX, 'column_');
			}

			return nextColumn;
		});

		const hasAnyContent = nextColumns.some(
			(column) => Array.isArray(column?.contents) && column.contents.length > 0
		);
		if (!hasAnyContent) {
			return null;
		}

		const nextRow = {
			...row,
			columns: nextColumns,
		};
		// Managed row ids are reserved for auto-inserted signature rows.
		// If the row still has user-authored content, rename it to avoid future
		// signature cleanup treating it as disposable.
		if (isManagedRow) {
			nextRow.id = normalizeManagedId(rowId, SIGNATURE_ROW_ID_PREFIX, 'row_');
		}

		return nextRow;
	});

	rows.length = 0;
	rows.push(...nextRows.filter(Boolean));
};
